/**
 * Configuration and persistence for the view version switcher.
 *
 * To register a new view, add an entry to VIEW_VERSION_MAP.
 * The key is the v3 (new) route name, the value is the legacy (old) route name.
 *
 * The "use legacy view" preference is stored per-route with the local calendar
 * day it was chosen (`savedOn`). Presence of an entry means the user opted into
 * the legacy view; choosing the new view deletes the entry. Entries are
 * considered expired once the local calendar day rolls over, so the user is
 * asked to re-opt-in once per day to nudge adoption of the new view.
 */

const STORAGE_KEY = 'view-version-preferences'

const VIEW_VERSION_MAP: Record<string, string> = {
	'cargo.projects': 'cargo.projects-old',
	'cargo.owned': 'cargo.owned-old',
	transports: 'transports-old'
}

const REVERSE_MAP: Record<string, string> = Object.fromEntries(
	Object.entries(VIEW_VERSION_MAP).map(([k, v]) => [v, k])
)

interface LegacyPreference {
	savedOn: string
}

type StoredPreferences = Record<string, LegacyPreference>

function todayKey(): string {
	const d = new Date()
	const yyyy = d.getFullYear()
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const dd = String(d.getDate()).padStart(2, '0')
	return `${yyyy}-${mm}-${dd}`
}

function isLegacyPreference(value: unknown): value is LegacyPreference {
	return (
		typeof value === 'object' &&
		value !== null &&
		typeof (value as LegacyPreference).savedOn === 'string'
	)
}

function getPreferences(): StoredPreferences {
	try {
		const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
		if (typeof raw !== 'object' || raw === null) return {}
		const prefs: StoredPreferences = {}
		for (const [key, value] of Object.entries(raw)) {
			if (isLegacyPreference(value)) prefs[key] = value
		}
		return prefs
	} catch {
		return {}
	}
}

function savePreference(newRouteName: string, prefersLegacy: boolean): void {
	const prefs = getPreferences()
	if (prefersLegacy) {
		prefs[newRouteName] = { savedOn: todayKey() }
	} else {
		delete prefs[newRouteName]
	}
	localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

/**
 * Returns the legacy route name if the user prefers the old view for a given v3 route.
 * Returns null if no preference, the preference is for the new view, or the
 * preference was set on a previous calendar day (expired).
 */
export function getPreferredLegacyRedirect(routeName: string): string | null {
	if (!(routeName in VIEW_VERSION_MAP)) return null
	const prefs = getPreferences()
	const entry = prefs[routeName]
	if (entry && entry.savedOn === todayKey()) return VIEW_VERSION_MAP[routeName]
	return null
}

export function isNewViewRoute(routeName: string): boolean {
	return routeName in VIEW_VERSION_MAP
}

export function isLegacyViewRoute(routeName: string): boolean {
	return routeName in REVERSE_MAP
}

export function getTargetRoute(routeName: string): string {
	if (routeName in VIEW_VERSION_MAP) return VIEW_VERSION_MAP[routeName]
	if (routeName in REVERSE_MAP) return REVERSE_MAP[routeName]
	return ''
}

/**
 * Gets the canonical (new) route name for a given route.
 * Works for both new and legacy route names.
 */
function getNewRouteName(routeName: string): string {
	if (routeName in VIEW_VERSION_MAP) return routeName
	if (routeName in REVERSE_MAP) return REVERSE_MAP[routeName]
	return ''
}

/**
 * Persist that the user prefers the target route.
 * Call this when the user clicks the switch button.
 */
export function persistViewPreference(currentRouteName: string, targetRouteName: string): void {
	const newRoute = getNewRouteName(currentRouteName)
	if (newRoute) {
		const prefersLegacy = isLegacyViewRoute(targetRouteName)
		savePreference(newRoute, prefersLegacy)
	}
}
